"use server";

import {
  addImmatriculationCodeRejetRecyclAutoSchema,
  editImmatriculationCodeRejetRecyclAutoSchema,
  deleteImmatriculationCodeRejetRecyclAutoSchema,
} from "@/lib/schemas/immat-code-rejet-recycl-auto";

import { validateParams } from "@/lib/utils";
import { prisma } from "@/prisma";

import z from "zod";
import { v4 as uuidv4 } from "uuid";

import { requireServerRole } from "@/lib/guards";
import { GRP_ROLE_ADMIN } from "@/lib/grp";

import { ActionResult } from "@/types/server-action";
import { OperationType } from "@/prisma/generated/enums";

export async function addImmatriculationCodeRejetRecyclAuto(
  params: z.infer<typeof addImmatriculationCodeRejetRecyclAutoSchema>,
): Promise<ActionResult> {
  await requireServerRole([GRP_ROLE_ADMIN]);

  try {
    const { operationType, rejectCode, startDate, endDate, isActive } =
      validateParams(addImmatriculationCodeRejetRecyclAutoSchema, params);

    const conflict = await prisma.immatriculationCodeRejetRecyclAuto.findFirst({
      where: {
        operationType: operationType as OperationType,
        rejectCode,
        deletedAt: null,
      },
    });

    if (conflict) {
      return {
        success: false,
        message:
          "Une règle existe déjà pour ce type d'opération et ce code rejet.",
      };
    }

    const item = await prisma.immatriculationCodeRejetRecyclAuto.create({
      data: {
        operationType: operationType as OperationType,
        rejectCode,
        startDate,
        endDate,
        isActive,
        versionGroupId: uuidv4(),
        versionNumber: 1,
      },
    });

    return {
      success: true,
      data: item,
    };
  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}

export async function editImmatriculationCodeRejetRecyclAuto(
  params: z.infer<typeof editImmatriculationCodeRejetRecyclAutoSchema>,
): Promise<ActionResult> {
  await requireServerRole([GRP_ROLE_ADMIN]);

  try {
    const { id, operationType, rejectCode, startDate, endDate, isActive } =
      validateParams(editImmatriculationCodeRejetRecyclAutoSchema, params);

    const current = await prisma.immatriculationCodeRejetRecyclAuto.findUnique({
      where: { id },
    });

    if (!current) {
      return {
        success: false,
        message: "Paramètre introuvable.",
      };
    }

    const duplicate = await prisma.immatriculationCodeRejetRecyclAuto.findFirst(
      {
        where: {
          id: {
            not: id,
          },
          operationType: operationType as OperationType,
          rejectCode,
          isActive,
          deletedAt: null,
        },
      },
    );

    if (duplicate && isActive) {
      return {
        success: false,
        message:
          "Une règle existe déjà pour ce type d'opération et ce code rejet.",
      };
    }

    const newVersion = await prisma.$transaction(async (tx) => {
      await tx.immatriculationCodeRejetRecyclAuto.update({
        where: {
          id: current.id,
        },
        data: {
          isActive: false,
        },
      });

      return tx.immatriculationCodeRejetRecyclAuto.create({
        data: {
          operationType: operationType as OperationType,
          rejectCode,
          startDate,
          endDate,
          isActive,

          versionGroupId: current.versionGroupId,

          versionNumber: current.versionNumber + 1,
        },
      });
    });

    return {
      success: true,
      data: newVersion,
    };
  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}

export async function deleteImmatriculationCodeRejetRecyclAuto(
  params: z.infer<typeof deleteImmatriculationCodeRejetRecyclAutoSchema>,
): Promise<ActionResult> {
  await requireServerRole([GRP_ROLE_ADMIN]);

  try {
    const { id } = validateParams(
      deleteImmatriculationCodeRejetRecyclAutoSchema,
      params,
    );

    const record = await prisma.immatriculationCodeRejetRecyclAuto.findUnique({
      where: { id },
    });

    if (!record) {
      return {
        success: false,
        message: "Enregistrement introuvable.",
      };
    }

    await prisma.immatriculationCodeRejetRecyclAuto.updateMany({
      where: {
        versionGroupId: record.versionGroupId,
      },
      data: {
        deletedAt: new Date(),
        isActive: false,
      },
    });

    return {
      success: true,
    };
  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}
